import { useRef } from 'react';
import { useFormContext } from './HBFormProvider';
import { getFieldValidationTests } from './useFormFieldProps';

type FormCtx = ReturnType<typeof useFormContext>;

/**
 * A flat, form-bound field bundle. Spread it directly onto HBFieldControl —
 * the chrome props (error/errorMessage/required) drive the label and message,
 * and the control props (value/onChange/onBlur/name/maxLength) are forwarded to
 * your control through the render slot.
 */
export interface HBFormFieldBundle {
    /** The field id — also the Formik field key and the `<label htmlFor>` target. */
    id: string;
    /** Form field name (equals the id). */
    name: string;
    /** Current value from Formik state. */
    value: any;
    /** Formik change handler. Relies on the control carrying a matching `name`. */
    onChange: (...args: any[]) => void;
    /** Formik blur handler (marks the field touched). */
    onBlur: (...args: any[]) => void;
    /** Sets the field value directly. Use for value-based controls (e.g. HBSelect) whose onChange emits a value rather than a DOM event. */
    setValue: (value: any) => void;
    /** Whether the field currently has a validation error. */
    error: boolean;
    /** The validation error message, when present. */
    errorMessage?: string;
    /** Derived from the form's Yup schema (`required()`), when a schema is provided. */
    required?: boolean;
    /** Derived from the form's Yup schema (`max()` on a string field), when present. */
    maxLength?: number;
}

const getValidationProps = (
    validationSchema: FormCtx['validationSchema'],
    id: string
): Pick<HBFormFieldBundle, 'required' | 'maxLength'> => {
    const parsed = getFieldValidationTests(validationSchema, id);
    if (!parsed) {
        return {};
    }
    const { required, max, type } = parsed;

    // Only emit `required` when the schema actually resolved one — the bundle is
    // spread after the consumer's own props (createHBFormField.tsx), so an emitted
    // `required: undefined` would overwrite a `required` the consumer passed
    // explicitly, even though the schema had no opinion on this field.
    return {
        ...(required !== undefined ? { required } : {}),
        ...(type !== 'number' && max ? { maxLength: max.max } : {})
    };
};

const buildBundle = (
    form: FormCtx,
    id: string,
    getStableSetValue: (id: string) => (value: any) => void
): HBFormFieldBundle => {
    const field = form.getFieldProps(id);
    const meta = form.getFieldMeta(id);
    return {
        id,
        name: field.name,
        value: field.value,
        onChange: field.onChange,
        onBlur: field.onBlur,
        setValue: getStableSetValue(id),
        error: !!meta.error,
        errorMessage: meta.error,
        ...getValidationProps(form.validationSchema, id)
    };
};

/**
 * Binds one or more fields to the surrounding HBFormProvider (Formik) and returns
 * flat bundle(s) to spread onto HBFieldControl.
 *
 * - Pass a single id → a single bundle.
 * - Pass an array of ids → an object keyed by id (`const { email, name } = useHBFormField(['email', 'name'])`).
 *
 * Must be called inside an HBFormProvider. Like all context-reading hooks, it
 * throws when reached across a portal boundary outside the provider subtree.
 *
 * Implementation note: reads the form via a single `useFormContext()` call and
 * derives each field with Formik's non-hook getters, so the array form never
 * violates the rules of hooks regardless of how many ids are passed.
 *
 * `setValue` is cached per id in a ref-backed map, so its identity stays stable
 * across renders — like Formik's own `setFieldValue` — safe to put in a
 * `useCallback`/`useEffect` dependency array without triggering on every render.
 */
export function useHBFormField(id: string): HBFormFieldBundle;
export function useHBFormField<const T extends readonly string[]>(
    ids: T
): Record<T[number], HBFormFieldBundle>;
export function useHBFormField(
    idOrIds: string | readonly string[]
): HBFormFieldBundle | Record<string, HBFormFieldBundle> {
    const setValueCacheRef = useRef<Map<string, (value: any) => void>>();
    if (!setValueCacheRef.current) {
        setValueCacheRef.current = new Map();
    }
    const setValueCache = setValueCacheRef.current;

    const form = useFormContext();
    if (!form) {
        throw new Error(
            'useHBFormField must be used within an HBFormProvider.'
        );
    }

    const getStableSetValue = (id: string) => {
        let setValue = setValueCache.get(id);
        if (!setValue) {
            setValue = value => {
                void form.setFieldValue(id, value);
            };
            setValueCache.set(id, setValue);
        }
        return setValue;
    };

    if (typeof idOrIds === 'string') {
        return buildBundle(form, idOrIds, getStableSetValue);
    }

    return idOrIds.reduce<Record<string, HBFormFieldBundle>>((acc, id) => {
        acc[id] = buildBundle(form, id, getStableSetValue);
        return acc;
    }, {});
}
